import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { TalentBoardComment } from '@bloomays-lib/types.shared';
import { UPSERT_TALENT_BOARD_COMMENT } from '@bloomays-lib/adapter.api-talent';
import { Logger } from '../services/service-logger';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';

const logger = Logger('useUpsertTalentBoardComment');

type UpsertTalentBoardCommentVariables = {
  id: string | null;
  talentId: string;
  jobId: string;
  comment: string;
  author: string;
};

export const useUpsertTalentBoardComment = () => {
  const [upsertTalentBoardCommentMutation, { loading, error }] = useMutation<
    { upsertTalentBoardComment: TalentBoardComment | null },
    UpsertTalentBoardCommentVariables
  >(UPSERT_TALENT_BOARD_COMMENT, {
    context: { clientName: 'api.talents' },
  });

  const execute = useCallback(
    async (variables: UpsertTalentBoardCommentVariables): Promise<TalentBoardComment | null> => {
      try {
        const result = await upsertTalentBoardCommentMutation({
          variables: {
            ...variables,
            id: variables.id ?? null,
          },
        });
        return result.data?.upsertTalentBoardComment ?? null;
      } catch (err) {
        logger.error('Error upserting talent board comment', variables, err as Error);
        monitorException(err as Error, { name: 'useUpsertTalentBoardComment', variables });
        return null;
      }
    },
    [upsertTalentBoardCommentMutation],
  );

  return {
    upsertTalentBoardComment: execute,
    loading,
    error,
  };
};

export default useUpsertTalentBoardComment;
